/**
 * Date range presets + global context for the admin panel.
 *
 * Most pages share a single window. The user picks a preset (or custom)
 * once and the choice is persisted in localStorage and applied to every
 * page that consumes `useDateRange()`.
 *
 * Backend integration: each preset/custom range converts to a
 * `{ days?, start?, end? }` triplet that every router accepts.
 *  - presets `today`, `yesterday`, `mtd`, `ytd`, `custom` → `start`/`end`
 *  - presets `l7d`, `l14d`, `l28d`, `l90d`, `l365d`         → `days`
 *
 * UI: see `DateRangeFilter.tsx`.
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'l7d'
  | 'l14d'
  | 'l28d'
  | 'l90d'
  | 'l365d'
  | 'mtd'
  | 'ytd'
  | 'custom';

export interface DateRange {
  preset: DateRangePreset;
  /** Inclusive start (UTC). */
  start: Date;
  /** Exclusive end (UTC). */
  end: Date;
}

export interface ApiRangeParams {
  /** Lookback in days, when using a `lN d` preset. */
  days?: number;
  /** ISO 8601 start (UTC). Set with `end` for absolute windows. */
  start?: string;
  /** ISO 8601 end (UTC). */
  end?: string;
}

const STORAGE_KEY = 'jq_admin_date_range_v1';

// ---------------------------------------------------------------------------
// Preset → DateRange resolver
// ---------------------------------------------------------------------------

function startOfDayUTC(d: Date): Date {
  const x = new Date(d);
  x.setUTCHours(0, 0, 0, 0);
  return x;
}

export function resolvePreset(preset: DateRangePreset): DateRange {
  const now = new Date();
  const todayStart = startOfDayUTC(now);

  switch (preset) {
    case 'today':
      return { preset, start: todayStart, end: now };
    case 'yesterday': {
      const start = new Date(todayStart);
      start.setUTCDate(start.getUTCDate() - 1);
      return { preset, start, end: todayStart };
    }
    case 'l7d':
      return { preset, start: addDays(now, -7), end: now };
    case 'l14d':
      return { preset, start: addDays(now, -14), end: now };
    case 'l28d':
      return { preset, start: addDays(now, -28), end: now };
    case 'l90d':
      return { preset, start: addDays(now, -90), end: now };
    case 'l365d':
      return { preset, start: addDays(now, -365), end: now };
    case 'mtd': {
      const start = new Date(todayStart);
      start.setUTCDate(1);
      return { preset, start, end: now };
    }
    case 'ytd': {
      const start = new Date(todayStart);
      start.setUTCMonth(0, 1);
      return { preset, start, end: now };
    }
    case 'custom':
      // Caller must provide explicit dates; default to L7D.
      return { preset, start: addDays(now, -7), end: now };
  }
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + days);
  return x;
}

// ---------------------------------------------------------------------------
// DateRange → API params (preferring `days` for stable rolling presets so
// the backend uses a fresh `now`, avoiding stale clocks between client/server).
// ---------------------------------------------------------------------------

export function rangeToApiParams(range: DateRange): ApiRangeParams {
  switch (range.preset) {
    case 'l7d':
      return { days: 7 };
    case 'l14d':
      return { days: 14 };
    case 'l28d':
      return { days: 28 };
    case 'l90d':
      return { days: 90 };
    case 'l365d':
      return { days: 365 };
    case 'today':
    case 'yesterday':
    case 'mtd':
    case 'ytd':
    case 'custom':
      return { start: range.start.toISOString(), end: range.end.toISOString() };
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const PRESET_LABELS: Record<DateRangePreset, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  l7d: 'Últimos 7d',
  l14d: 'Últimos 14d',
  l28d: 'Últimos 28d',
  l90d: 'Últimos 90d',
  l365d: 'Últimos 365d',
  mtd: 'Mês até hoje',
  ytd: 'Ano até hoje',
  custom: 'Personalizado',
};

export const PRESET_ORDER: DateRangePreset[] = [
  'today',
  'yesterday',
  'l7d',
  'l14d',
  'l28d',
  'l90d',
  'mtd',
  'ytd',
];

export function formatRangeShort(range: DateRange): string {
  if (range.preset !== 'custom') return PRESET_LABELS[range.preset];
  const fmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  return `${fmt.format(range.start)} → ${fmt.format(range.end)}`;
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

interface PersistShape {
  preset: DateRangePreset;
  start?: string;
  end?: string;
}

function loadPersisted(): DateRange | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistShape;
    if (parsed.preset === 'custom' && parsed.start && parsed.end) {
      return {
        preset: 'custom',
        start: new Date(parsed.start),
        end: new Date(parsed.end),
      };
    }
    if (parsed.preset && parsed.preset in PRESET_LABELS) {
      return resolvePreset(parsed.preset);
    }
  } catch {
    // localStorage unavailable or corrupt → fall through to default.
  }
  return null;
}

function persist(range: DateRange) {
  try {
    const payload: PersistShape = { preset: range.preset };
    if (range.preset === 'custom') {
      payload.start = range.start.toISOString();
      payload.end = range.end.toISOString();
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore: best-effort persistence
  }
}

// ---------------------------------------------------------------------------
// React context
// ---------------------------------------------------------------------------

interface DateRangeCtx {
  range: DateRange;
  setPreset: (p: DateRangePreset) => void;
  setCustom: (start: Date, end: Date) => void;
  apiParams: ApiRangeParams;
}

const Ctx = createContext<DateRangeCtx | null>(null);

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [range, setRange] = useState<DateRange>(
    () => loadPersisted() ?? resolvePreset('l7d'),
  );

  // Refresh "rolling" presets every 5 min so DAU/WAU stay current even if
  // the dashboard is left open.
  useEffect(() => {
    if (range.preset === 'custom') return;
    const id = setInterval(() => {
      setRange((cur) =>
        cur.preset === 'custom' ? cur : resolvePreset(cur.preset),
      );
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [range.preset]);

  const setPreset = useCallback((p: DateRangePreset) => {
    const next = resolvePreset(p);
    persist(next);
    setRange(next);
  }, []);

  const setCustom = useCallback((start: Date, end: Date) => {
    if (end <= start) return;
    const next: DateRange = { preset: 'custom', start, end };
    persist(next);
    setRange(next);
  }, []);

  const apiParams = useMemo(() => rangeToApiParams(range), [range]);

  const value = useMemo(
    () => ({ range, setPreset, setCustom, apiParams }),
    [range, setPreset, setCustom, apiParams],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDateRange(): DateRangeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDateRange must be used inside <DateRangeProvider>');
  return v;
}

/**
 * Stable string key (for react-query) representing the current range.
 * Components can include this in queryKeys to refetch when the range changes.
 */
export function useDateRangeKey(): string {
  const { range } = useDateRange();
  if (range.preset !== 'custom') return range.preset;
  return `${range.preset}:${range.start.toISOString()}:${range.end.toISOString()}`;
}
